import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  formatCustomUnitMeasurementEquivalenceSuffix,
  parseUnitToShow,
} from "../../../../../utils/nutrition-util";
import { MacrosDisplay } from "./MacrosDisplay";

/**
 * Shared content for the meal planner drag overlay (individual food or custom meal).
 * Used by MealPlannerWeekResume and MealPlannerOptionResume.
 */
export function MealPlannerDragOverlayContent({ activeId, activeData }) {
  const { t } = useTranslation("athlete-portal");
  if (!activeId || !activeData) return null;

  if (activeData.type === "food") {
    const food = activeData.foodData;
    const foodId = food?.foodId;
    const displayName =
      foodId?.brand && !foodId?.name?.toLowerCase().includes(foodId.brand.toLowerCase())
        ? `${foodId.name} (${foodId.brand})`
        : foodId?.name || t("mealPlannerDragOverlay.foodFallback");

    const quantityNumber = food?.quantityNumber;
    const foodType = food?.type ?? food?.foodType;
    const unitOfMeasure = food?.unitOfMeasure;
    const quantityDescription = food?.quantityDescription ?? foodId?.quantityDescription ?? "";
    const quantityBase =
      quantityNumber == null
        ? ""
        : `${quantityNumber} ${parseUnitToShow(
            quantityNumber,
            foodType ?? "food",
            unitOfMeasure ?? "g",
            quantityDescription
          )}`;
    const quantityDisplay =
      quantityBase +
      (foodId
        ? formatCustomUnitMeasurementEquivalenceSuffix(
            quantityNumber,
            unitOfMeasure,
            foodId
          )
        : "");

    return (
      <div className="full-info-meal-planner-overlay">
        <div className="grouped-meal-header-content">
          <span className="title">
            {displayName}
          </span>
          {quantityDisplay && (
            <span className="subtitle">
              {quantityDisplay}
            </span>
          )}
        </div>

        {foodId && (
          <MacrosDisplay
            proteins={foodId.proteins || 0}
            carbs={foodId.carbs || 0}
            fats={foodId.fats || 0}
            calories={foodId.calories || 0}
          />
        )}
      </div>
    );
  }

  if (activeData.type === "customMeal") {
    const foodsOfMeal = activeData.foodsOfMeal || [];
    const totals = foodsOfMeal.reduce(
      (acc, food) => {
        if (food?.foodId) {
          acc.proteins += food.foodId.proteins || 0;
          acc.carbs += food.foodId.carbs || 0;
          acc.fats += food.foodId.fats || 0;
          acc.calories += food.foodId.calories || 0;
        }
        return acc;
      },
      { proteins: 0, carbs: 0, fats: 0, calories: 0 }
    );

    return (
      <div className="grouped-meal full-info-meal-planner-overlay">
        <div className="grouped-meal-header-content">
          <span className="title">
            {activeData.meal?.name || t("mealPlannerDragOverlay.mealFallback")}
          </span>
          {activeData.meal?.description && (
            <span className="subtitle pb-1.5">
              {activeData.meal.description}
            </span>
          )}
        </div>

        {foodsOfMeal.map((food) => {
          const foodId = food?.foodId;
          if (!foodId) return null;
          const { type: foodType, quantityNumber, unitOfMeasure } = food;
          const { name, brand, quantityDescription, _id: catalogId } = foodId;
          const foodDisplayName =
            brand && !name?.toLowerCase().includes(brand.toLowerCase())
              ? `${name} (${brand})`
              : name;

          const itemKey = food?._id ?? `${catalogId ?? "food"}-${foodDisplayName}-${quantityNumber}`;

          const unitBase =
            quantityNumber == null
              ? ""
              : `${quantityNumber} ${parseUnitToShow(
                  quantityNumber,
                  foodType ?? "food",
                  unitOfMeasure ?? "g",
                  quantityDescription ?? ""
                )}`;
          const unitDisplay =
            unitBase +
            formatCustomUnitMeasurementEquivalenceSuffix(
              quantityNumber,
              unitOfMeasure,
              foodId
            );

          return (
            <div key={itemKey} className="meal-foods">
              <div className="meal-foods-info">
                <div className="meal-foods-info-food">
                  <div className="meal-foods-info-title">
                    {foodDisplayName}
                  </div>
                  <div className="meal-foods-info-subtitle">
                    {unitDisplay}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <MacrosDisplay
          proteins={totals.proteins}
          carbs={totals.carbs}
          fats={totals.fats}
          calories={totals.calories}
          className="values meal-values"
        />
      </div>
    );
  }

  return null;
}

MealPlannerDragOverlayContent.propTypes = {
  activeId: PropTypes.string,
  activeData: PropTypes.shape({
    type: PropTypes.oneOf(["food", "customMeal"]),
    foodData: PropTypes.object,
    meal: PropTypes.object,
    foodsOfMeal: PropTypes.array,
  }),
};
